import { Controller } from "@hotwired/stimulus"

// Aggregates the server-rendered form field partials (app/views/forms/fields/)
// into the hidden event_registration[form_response] field, computes the
// live fee, and runs a pared-down client-side validator. It never builds
// HTML — fields are already in the DOM; list rows are added by cloning a
// <template> (see _list_field.html.erb).
export default class extends Controller {
  static get targets() {
    return ["formResponseHidden", "feeTotal"]
  }

  static get values() {
    return {
      registrationFee: Number,
      showFee: Boolean
    }
  }

  connect() {
    this.formResponse = this.parseFormResponse()
    this.formElement = this.element.closest("form")
    this.validateListener = this.handleValidateRequest.bind(this)
    if (this.formElement) {
      this.formElement.addEventListener("registration:validate-form-response", this.validateListener)
    }

    // select_field/quantity_field default their response from whatever the
    // browser currently shows (first <option>, or the rendered "0"), which
    // the retired JS controller used to synthesize itself on connect.
    this.fieldWrappers()
      .filter((wrapper) => ["select_field", "quantity_field"].includes(wrapper.dataset.fieldType))
      .forEach((wrapper) => this.updateFromWrapper(wrapper, null))

    this.persist()
  }

  disconnect() {
    if (this.formElement) {
      this.formElement.removeEventListener("registration:validate-form-response", this.validateListener)
    }
  }

  // Bound via data-action="input->...#sync change->...#sync" on the
  // controller's root element; bubbled input/change events from any
  // descendant field are caught here.
  sync(event) {
    const wrapper = event.target.closest("[data-form-field]")
    if (!wrapper) {
      return
    }

    this.updateFromWrapper(wrapper, event.target)
    this.persist()
  }

  incrementQuantity(event) {
    this.stepQuantity(event, 1)
  }

  decrementQuantity(event) {
    this.stepQuantity(event, -1)
  }

  stepQuantity(event, delta) {
    const wrapper = event.target.closest("[data-form-field]")
    const input = wrapper.querySelector("[data-field-input]")
    const quantity = Math.max(0, Number(input.value || 0) + delta)
    input.value = quantity
    wrapper.querySelector("[data-quantity-display]").textContent = quantity
    this.updateFromWrapper(wrapper, input)
    this.persist()
  }

  addListItem(event) {
    const container = event.currentTarget.closest("[data-list-container]")
    const template = container.querySelector("template[data-list-item-template]")
    container.querySelector("[data-list-items]").appendChild(template.content.cloneNode(true))
    this.updateFromWrapper(container.closest("[data-form-field]"), null)
    this.persist()
  }

  removeListItem(event) {
    const row = event.currentTarget.closest("[data-list-item]")
    const wrapper = row.closest("[data-form-field]")
    row.remove()
    this.updateFromWrapper(wrapper, null)
    this.persist()
  }

  // -- response aggregation -------------------------------------------

  updateFromWrapper(wrapper, target) {
    const fieldType = wrapper.dataset.fieldType
    const entry = {
      field_id: wrapper.dataset.fieldId,
      field_name: wrapper.dataset.fieldName,
      field_type: fieldType
    }

    if (["text_field", "textarea_field", "phone_number_field"].includes(fieldType)) {
      entry.field_value = wrapper.querySelector("[data-field-input]").value
    } else if (fieldType === "radio_field") {
      const checked = wrapper.querySelector("input[type=radio]:checked")
      if (!checked) {
        return
      }
      entry.field_value = checked.value
      entry.field_view_label = checked.dataset.optionLabel
      entry.field_fee_modifiers = [this.parseModifier(checked.dataset.optionModifier)].filter(Boolean)
    } else if (fieldType === "select_field") {
      const select = wrapper.querySelector("select")
      const option = select.selectedOptions[0]
      entry.field_value = select.value
      entry.field_view_label = option?.dataset.optionLabel
      entry.field_fee_modifiers = [this.parseModifier(option?.dataset.optionModifier)].filter(Boolean)
    } else if (fieldType === "checkbox_field") {
      const checked = Array.from(wrapper.querySelectorAll("input[type=checkbox]:checked"))
      const modifiers = checked.map((box) => this.parseModifier(box.dataset.optionModifier)).filter(Boolean)
      entry.field_value = checked.map((box) => box.value)
      entry.field_view_label = checked.map((box) => box.dataset.optionLabel)
      entry.field_fee_modifiers = modifiers.length > 0 ? modifiers : null
    } else if (fieldType === "uk_address_field") {
      const value = { ...(this.fieldResponse(entry.field_id)?.field_value || {}) }
      if (target?.dataset.addressPart) {
        value[target.dataset.addressPart] = target.value
      }
      entry.field_value = value
    } else if (fieldType === "quantity_field") {
      entry.field_value = Number(wrapper.querySelector("[data-field-input]").value || 0)
      const modifier = this.parseModifier(wrapper.dataset.fieldModifier)
      entry.field_fee_modifiers = modifier ? [modifier] : []
    } else if (fieldType === "list_field") {
      const rows = Array.from(wrapper.querySelectorAll("[data-list-item]"))
      entry.field_value = rows.map((row, index) => ({
        value: row.querySelector("[data-field-input]").value,
        position: index
      }))
      const modifier = this.parseModifier(wrapper.dataset.fieldModifier)
      entry.field_fee_modifiers = modifier ? [modifier] : []
    } else {
      return
    }

    this.upsertResponse(entry)
  }

  upsertResponse(entry) {
    const index = this.formResponse.findIndex(
      (response) => String(response.field_id) === String(entry.field_id)
    )
    if (index === -1) {
      this.formResponse.push(entry)
    } else {
      this.formResponse[index] = entry
    }
  }

  fieldResponse(fieldId) {
    return this.formResponse.find((response) => String(response.field_id) === String(fieldId))
  }

  parseFormResponse() {
    try {
      const parsed = JSON.parse(this.formResponseHiddenTarget.value || "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch (error) {
      return []
    }
  }

  parseModifier(json) {
    if (!json) {
      return null
    }
    try {
      return JSON.parse(json)
    } catch (error) {
      return null
    }
  }

  fieldWrappers() {
    return Array.from(this.element.querySelectorAll("[data-form-field]"))
  }

  persist() {
    this.formResponseHiddenTarget.value = JSON.stringify(this.formResponse)
    this.updateFee()
  }

  // -- fee ---------------------------------------------------------------

  updateFee() {
    if (!this.showFeeValue || !this.hasFeeTotalTarget) {
      return
    }

    this.feeTotalTarget.textContent = `£${this.calculateFee().toFixed(2)}`
  }

  calculateFee() {
    let total = Number(this.registrationFeeValue || 0)

    this.formResponse.forEach((response) => {
      const modifiers = Array.isArray(response.field_fee_modifiers)
        ? response.field_fee_modifiers.filter(Boolean)
        : []

      modifiers.forEach((modifier) => {
        const modifierValue = Number(modifier.modifier_value || 0)
        if (modifier.modifier_type === "fixed_amount") {
          total += modifierValue
        }
        if (modifier.modifier_type === "multiplier") {
          total += modifierValue * Number(response.field_value || 0)
        }
        if (modifier.modifier_type === "count_multiplier") {
          const count = Array.isArray(response.field_value) ? response.field_value.length : 0
          total += modifierValue * count
        }
      })
    })

    return Math.max(0, total / 100)
  }

  // -- validation ----------------------------------------------------

  handleValidateRequest(event) {
    const errors = this.validateResponse()

    this.fieldWrappers().forEach((wrapper) => {
      const message = errors.get(wrapper.dataset.fieldId)
      wrapper.classList.toggle("field_with_errors", Boolean(message))
      const errorEl = wrapper.querySelector("[data-field-error]")
      if (errorEl) {
        errorEl.textContent = message || ""
        errorEl.hidden = !message
      }
    })

    event.detail.valid = errors.size === 0
  }

  validateResponse() {
    const errors = new Map()

    this.fieldWrappers().forEach((wrapper) => {
      const fieldId = wrapper.dataset.fieldId
      const fieldType = wrapper.dataset.fieldType
      const required = wrapper.dataset.required === "true"
      const response = this.fieldResponse(fieldId)

      if (!required && !this.isPopulated(response)) {
        return
      }

      if (fieldType === "text_field" || fieldType === "textarea_field") {
        if (!this.isPopulated(response) || this.isBlank(response.field_value)) {
          errors.set(fieldId, "This field is required.")
        }
      } else if (fieldType === "phone_number_field") {
        if (!this.isPopulated(response)) {
          errors.set(fieldId, "Please add a phone number.")
        } else if (!/^(?=.*\d)[\d +]+$/.test(response.field_value || "")) {
          errors.set(fieldId, "Please add a valid phone number. Numbers and spaces only.")
        }
      } else if (fieldType === "uk_address_field") {
        const value = response?.field_value || {}
        if (
          this.isBlank(value.address_1) ||
          this.isBlank(value.county) ||
          this.isBlank(value.town) ||
          this.isBlank(value.postcode)
        ) {
          errors.set(fieldId, "Please add a valid UK address.")
        }
      } else if (fieldType === "radio_field" || fieldType === "select_field") {
        if (!this.isPopulated(response)) {
          errors.set(fieldId, "Please select an option.")
        }
      } else if (fieldType === "checkbox_field") {
        if (!this.isPopulated(response)) {
          errors.set(fieldId, "Please select at least one option.")
        }
      } else if (fieldType === "list_field") {
        const values = response?.field_value || []
        if (values.some((item) => this.isBlank(item.value))) {
          errors.set(fieldId, "List items can't be blank. Remove items you don't need.")
        }
      }
    })

    return errors
  }

  isPopulated(response) {
    if (!response || response.field_value === undefined || response.field_value === null) {
      return false
    }

    if (Array.isArray(response.field_value)) {
      return response.field_value.length > 0
    }

    if (typeof response.field_value === "object") {
      return Object.keys(response.field_value).length > 0
    }

    return !this.isBlank(response.field_value)
  }

  isBlank(value) {
    return !value || /^\s*$/.test(String(value))
  }
}
